import { useCallback, useReducer } from "react";
import { useNavigate } from "react-router-dom";

import api from "utils/api";
import { createContainer } from "utils/context";
import { removeCookie } from "utils/cookie";
import { persist, hydrate } from "utils/persist";
import { AUTH_TOKEN_COOKIE } from "utils/api/getTokens";
import { AUTH_ACTIONS } from "./actions";
import { authReducer, initialState } from "./reducers";
import { RESPONSE, USER_DATA } from "utils/constants";

export const { useContext: useAuth, Provider: AuthProvider } = createContainer(
  () => {
    const [state, dispatch] = useReducer(authReducer, {
      ...initialState,
      isLoggedIn:
        !!hydrate(AUTH_TOKEN_COOKIE) &&
        hydrate(AUTH_TOKEN_COOKIE) !== "null" &&
        hydrate(USER_DATA),
      userData: hydrate(USER_DATA),
    });
    const navigate = useNavigate();

    // update user data
    const updateUserData = useCallback(
      (data) => {
        persist(USER_DATA, { ...state?.userData, ...data });
        dispatch(AUTH_ACTIONS.updateUserData({ ...state?.userData, ...data }));
      },
      [state?.userData]
    );

    // login
    const login = useCallback(
      async (email, password) => {
        try {
          const reqData = {
            email,
            password,
          };
          const { data } = await api("/admin/auth/login", {
            method: "post",
            data: reqData,
          });

          data?.status === RESPONSE.success && updateUserData({ email });
          return data?.status === RESPONSE.success;
        } catch (e) {
          throw e;
        }
      },
      [updateUserData]
    );

    // login 2fa otp
    const verifyOtp = useCallback(
      async (email, otp, isCaching) => {
        try {
          const reqData = {
            email,
            otp,
          };
          const { data } = await api("/admin/auth/verify", {
            method: "post",
            data: reqData,
          });

          if (isCaching && data?.status === RESPONSE.success) {
            // caching user
            updateUserData({ ...data?.data });
            // caching auth token
            persist(AUTH_TOKEN_COOKIE, data?.data?.token);
            // changing state
            dispatch(AUTH_ACTIONS.login.success(data?.data));
          }
          return data?.status === RESPONSE.success;
        } catch (e) {
          throw e;
        }
      },
      [updateUserData]
    );

    // otp resend
    const resendOtp = useCallback(async (email) => {
      try {
        const reqData = {
          email,
        };
        const { data } = await api("/admin/auth/resend", {
          method: "post",
          data: reqData,
        });

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    // logout
    const logout = useCallback(async () => {
      try {
        await api("/admin/auth/logout", {
          method: "post",
        });
      } catch (e) {}
      dispatch(AUTH_ACTIONS.logout);
      removeCookie(AUTH_TOKEN_COOKIE);
      removeCookie(USER_DATA);
      navigate("/");
    }, [navigate]);

    // forgot password
    const forgotPassword = useCallback(
      async (email) => {
        try {
          const reqData = {
            email,
          };
          const { data } = await api("/admin/auth/forgot", {
            method: "post",
            data: reqData,
          });

          data?.status === RESPONSE.success && updateUserData({ email });
          return data?.status === RESPONSE.success;
        } catch (e) {
          throw e;
        }
      },
      [updateUserData]
    );

    // resend forgot password
    const resendForgotPassword = useCallback(async (email) => {
      try {
        const reqData = {
          email,
        };
        const { data } = await api("/admin/auth/forgot/resend", {
          method: "post",
          data: reqData,
        });

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    // reset password
    const resetPassword = useCallback(async (email, password) => {
      try {
        const reqData = {
          email,
          password,
        };
        const { data } = await api("/admin/auth/reset", {
          method: "post",
          data: reqData,
        });

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);
    // change password
    const changePassword = useCallback(async ({ oldPassword, newPassword }) => {
      try {
        const reqData = {
          oldPassword,
          newPassword,
        };
        const { data } = await api("/admin/auth/changePassword", {
          method: "put",
          data: reqData,
        });

        return data?.status === RESPONSE.success;
      } catch (e) {
        throw e;
      }
    }, []);

    // update user
    const updateUser = useCallback(
      async ({ name }) => {
        try {
          const reqData = {
            name,
          };
          const { data } = await api("/admin/auth/update/profile", {
            method: "put",
            data: reqData,
          });
          data?.status === RESPONSE.success && updateUserData({ name });
          return data?.status === RESPONSE.success;
        } catch (e) {
          throw e;
        }
      },
      [updateUserData]
    );

    return {
      state,
      actions: {
        login,
        verifyOtp,
        resendOtp,
        logout,
        forgotPassword,
        resendForgotPassword,
        resetPassword,
        changePassword,
        updateUser,
      },
    };
  }
);
